import React, { FunctionComponent } from 'react';

import { List, ListItem, Text } from 'components/chroma';
import SummaryCard from 'components/SummaryCard';

import {
  toAussieStandardDateFormat,
  toDDMMYYYY,
} from 'scenes/common/helpers/datetime';

import {
  LandlordFullPageProps,
  LandlordPolicySubSection,
  LandlordQuoteSubSection,
} from 'scenes/landlord/types';
import {
  getAdditionalPolicyHolders,
  getBusinessAbn,
  getBusinessContactFirstName,
  getBusinessContactLastName,
  getBusinessName,
  getBusinessOwnerDateOfBirth,
  getContactEmailAddress,
  getContactPhoneNumber,
  getFlybuysNumber,
  getHistoryDetails,
  getLockedBoxAddress,
  getMainAddress,
  getPersonalContactFirstName,
  getPersonalContactLastName,
  getPolicyHolderDateOfBirth,
  getPolicyIssued,
  getPolicyStartDate,
  getPostalAddress,
  isPolicyIssuedToPerson,
  isPostalAddressLockedBagGpo,
  samePostalAddress,
} from 'scenes/landlord/selectors';
import { HistoryDetail } from 'scenes/common/flows/policyDetails/types';
import { LandlordFunnelState } from 'scenes/landlord/flows/types';
import { toDisplayAddress } from 'scenes/common/helpers/address';

const findPostalAddress = (funnelState: LandlordFunnelState) => {
  if (samePostalAddress(funnelState)) {
    return getMainAddress(funnelState);
  }
  if (isPostalAddressLockedBagGpo(funnelState)) {
    return getLockedBoxAddress(funnelState);
  }
  return getPostalAddress(funnelState);
};

const BusinessSummary: FunctionComponent<Pick<
  LandlordFullPageProps,
  'funnelState' | 'onEditQuoteDetails' | 'onEditPolicyDetails'
>> = ({ funnelState, onEditQuoteDetails, onEditPolicyDetails }) => (
  <>
    <SummaryCard
      heading="Business details"
      onEditClick={() =>
        onEditPolicyDetails(LandlordPolicySubSection.BusinessNameAndAbn)
      }
    >
      <Text>{getBusinessName(funnelState)}</Text>
      <Text>ABN: {getBusinessAbn(funnelState)}</Text>
    </SummaryCard>
    <SummaryCard
      heading="Business contact's name"
      onEditClick={() =>
        onEditPolicyDetails(LandlordPolicySubSection.BusinessPolicyContact)
      }
    >
      <Text>
        {`${getBusinessContactFirstName(
          funnelState
        )} ${getBusinessContactLastName(funnelState)}`}
      </Text>
    </SummaryCard>
    <SummaryCard
      heading="Business owner's date of birth"
      onEditClick={() =>
        onEditQuoteDetails(LandlordQuoteSubSection.ContactDateOfBirth)
      }
    >
      <Text>
        {toAussieStandardDateFormat(getBusinessOwnerDateOfBirth(funnelState))}
      </Text>
    </SummaryCard>
  </>
);

const PersonSummary: FunctionComponent<Pick<
  LandlordFullPageProps,
  'funnelState' | 'onEditQuoteDetails' | 'onEditPolicyDetails'
>> = ({ funnelState, onEditQuoteDetails, onEditPolicyDetails }) => (
  <>
    <SummaryCard
      heading="Main policy holder's name"
      onEditClick={() =>
        onEditPolicyDetails(LandlordPolicySubSection.PersonalPolicyContact)
      }
    >
      <Text>
        {`${getPersonalContactFirstName(
          funnelState
        )} ${getPersonalContactLastName(funnelState)}`}
      </Text>
    </SummaryCard>
    <SummaryCard
      heading="Main policy holder's date of birth"
      onEditClick={() =>
        onEditQuoteDetails(LandlordQuoteSubSection.ContactDateOfBirth)
      }
    >
      <Text>
        {toAussieStandardDateFormat(getPolicyHolderDateOfBirth(funnelState))}
      </Text>
    </SummaryCard>
  </>
);

export const PolicyDetailsSummary: FunctionComponent<Pick<
  LandlordFullPageProps,
  'funnelState' | 'onEditQuoteDetails' | 'onEditPolicyDetails'
>> = ({ funnelState, onEditQuoteDetails, onEditPolicyDetails }) => {
  const additionalPolicyHolders = getAdditionalPolicyHolders(funnelState);

  const specialDetails = {
    InsuranceClaimRefused: getHistoryDetails(funnelState).find(
      (sd) => sd === HistoryDetail.InsuranceClaimRefused
    ),
    DeniedPurchase: getHistoryDetails(funnelState).find(
      (sd) => sd === HistoryDetail.DeniedPurchase
    ),
    ConvictedBurglary: getHistoryDetails(funnelState).find(
      (sd) => sd === HistoryDetail.ConvictedBurglary
    ),
    ConvictedCriminalOffence: getHistoryDetails(funnelState).find(
      (sd) => sd === HistoryDetail.ConvictedCriminalOffence
    ),
  };

  return (
    <>
      <SummaryCard
        heading="Policy start date"
        onEditClick={() =>
          onEditQuoteDetails(LandlordQuoteSubSection.PolicyStartDate)
        }
      >
        <Text>{`${toAussieStandardDateFormat(
          toDDMMYYYY(getPolicyStartDate(funnelState))
        )}`}</Text>
      </SummaryCard>
      <SummaryCard
        heading="Policy issued to a person or business?"
        onEditClick={() =>
          onEditQuoteDetails(LandlordQuoteSubSection.IssuedToPersonOrBusiness)
        }
      >
        <Text>{getPolicyIssued(funnelState)}</Text>
      </SummaryCard>
      {isPolicyIssuedToPerson(funnelState) ? (
        <PersonSummary
          funnelState={funnelState}
          onEditQuoteDetails={onEditQuoteDetails}
          onEditPolicyDetails={onEditPolicyDetails}
        />
      ) : (
        <BusinessSummary
          funnelState={funnelState}
          onEditQuoteDetails={onEditQuoteDetails}
          onEditPolicyDetails={onEditPolicyDetails}
        />
      )}

      <SummaryCard
        heading="Additional policy holder(s)"
        onEditClick={() =>
          onEditPolicyDetails(LandlordPolicySubSection.AdditionalPolicyHolders)
        }
      >
        {additionalPolicyHolders.length > 0 ? (
          additionalPolicyHolders.map(({ firstName, lastName, dob }) => (
            <React.Fragment key={`${firstName}${lastName}`}>
              <Text weight={600} margin={{ bottom: 0 }}>
                {firstName} {lastName}
              </Text>
              <Text>{toAussieStandardDateFormat(dob)}</Text>
            </React.Fragment>
          ))
        ) : (
          <Text>None</Text>
        )}
      </SummaryCard>

      <SummaryCard
        heading="Contact phone no."
        onEditClick={() =>
          onEditPolicyDetails(LandlordPolicySubSection.ContactPhoneNumber)
        }
      >
        <Text>{getContactPhoneNumber(funnelState)}</Text>
      </SummaryCard>
      <SummaryCard
        heading="Contact email address"
        onEditClick={() =>
          onEditPolicyDetails(LandlordPolicySubSection.ContactEmailAddress)
        }
      >
        <Text>{getContactEmailAddress(funnelState)}</Text>
      </SummaryCard>
      <SummaryCard
        heading="Contact postal address"
        onEditClick={() =>
          onEditPolicyDetails(LandlordPolicySubSection.PostalAddress)
        }
      >
        <Text>{toDisplayAddress(findPostalAddress(funnelState))}</Text>
      </SummaryCard>

      <SummaryCard
        heading="Policy holder's declaration"
        onEditClick={() =>
          onEditPolicyDetails(LandlordPolicySubSection.PolicyHolderDeclaration)
        }
      >
        <List bullet>
          <ListItem>
            <Text>
              {specialDetails.InsuranceClaimRefused
                ? 'Had insurance claim refused'
                : 'No insurance claim refused'}
            </Text>
          </ListItem>
          <ListItem>
            <Text>
              {specialDetails.DeniedPurchase
                ? 'Have been denied purchase of new or renewed insurance'
                : 'Have not been denied purchase of new or renewed insurance'}
            </Text>
          </ListItem>
          <ListItem>
            <Text>
              {specialDetails.ConvictedBurglary
                ? 'Have been covicted of burglary, theft, arson or fraud'
                : 'Have not been covicted of burglary, theft, arson or fraud'}
            </Text>
          </ListItem>
          <ListItem>
            <Text>
              {specialDetails.ConvictedCriminalOffence
                ? 'Have been convicted of some other criminal offence'
                : 'Have not been convicted of some other criminal offence'}
            </Text>
          </ListItem>
        </List>
      </SummaryCard>

      <SummaryCard
        heading="Flybuys membership no."
        onEditClick={() =>
          onEditQuoteDetails(LandlordQuoteSubSection.FlybuysNumber)
        }
      >
        <Text>
          {getFlybuysNumber(funnelState)
            ? `6008 XXXX XXXX ${getFlybuysNumber(funnelState).substring(
                getFlybuysNumber(funnelState).length - 4
              )}`
            : 'None'}
        </Text>
      </SummaryCard>
    </>
  );
};

export default PolicyDetailsSummary;
